const recommendationCache = new Map();

async function fetchRecommendations(url, context) {
  const key = `${url.href}:${context}`;
  if (!recommendationCache.has(key)) {
    if (recommendationCache.size >= 12) recommendationCache.delete(recommendationCache.keys().next().value);
    recommendationCache.set(key, fetch(url, { signal: AbortSignal.timeout(5000) }).then(response => {
      if (!response.ok) throw new Error('Recommendations unavailable');
      return response.text();
    }).catch(error => {
      recommendationCache.delete(key);
      throw error;
    }));
  }
  const html = await recommendationCache.get(key);
  return new DOMParser().parseFromString(html, 'text/html');
}

class NativeCartRecommendations extends HTMLElement {
  #drawer;
  #loading = false;
  #loadedKey;
  #emptyResult = false;
  #observer = new MutationObserver(() => this.#loadIfVisible());
  #stateKey = () => `${this.dataset.url}:${this.dataset.productId}:${this.dataset.currency}:${this.dataset.cartProducts}`;
  #loadIfVisible = () => {
    if (!this.#drawer || this.#drawer.querySelector('dialog[open]')) this.load();
  };

  connectedCallback() {
    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener('dialog:open', this.load);
    this.#observer.observe(this, {
      childList: true, attributes: true,
      attributeFilter: ['data-product-id', 'data-cart-products', 'data-currency', 'data-url'],
    });
    this.#loadIfVisible();
  }

  disconnectedCallback() {
    this.#drawer?.removeEventListener('dialog:open', this.load);
    this.#observer.disconnect();
  }

  load = async () => {
    const stateKey = this.#stateKey();
    if (this.#loading || (this.#loadedKey === stateKey && (this.childElementCount || this.#emptyResult))) return;
    this.#loading = true;
    const context = `${this.dataset.currency}:${this.dataset.cartProducts}`;
    const excluded = new Set(this.dataset.cartProducts.split(','));
    const feeds = ['related', 'complementary'].map(intent => ({ intent, cards: [], document: null }));
    const takeCards = (document, feed) => {
      const cards = document?.querySelector('[data-recommendation-products]')?.content.children || [];
      for (const card of Array.from(cards)) {
        if (feed.cards.length >= 5) break;
        if (excluded.has(card.dataset.productId)) continue;
        excluded.add(card.dataset.productId);
        feed.cards.push(card.cloneNode(true));
      }
    };
    try {
      if (this.dataset.productId) {
        await Promise.all(feeds.map(async feed => {
          const url = new URL(this.dataset.url, location.origin);
          url.searchParams.set('section_id', 'cart-drawer-recommendations');
          url.searchParams.set('product_id', this.dataset.productId);
          url.searchParams.set('limit', '10');
          url.searchParams.set('intent', feed.intent);
          try { feed.document = await fetchRecommendations(url, context); } catch { /* Use collection fallback. */ }
        }));
        for (const feed of feeds) takeCards(feed.document, feed);
      }
      if (feeds.some(feed => feed.cards.length < 5)) {
        const url = new URL(this.dataset.fallbackUrl, location.origin);
        url.searchParams.set('section_id', 'cart-drawer-recommendations');
        try {
          const fallback = await fetchRecommendations(url, context);
          for (const feed of feeds) {
            takeCards(fallback, feed);
            feed.document ||= fallback;
          }
        } catch { /* Keep any successful recommendation results. */ }
      }
      if (!this.isConnected || this.#stateKey() !== stateKey) return;
      const rows = [];
      for (const feed of feeds) {
        if (!feed.cards.length) continue;
        const row = feed.document?.querySelector(`[data-recommendation-row="${feed.intent}"]`)?.content.firstElementChild?.cloneNode(true);
        if (!row) continue;
        row.querySelector('[data-carousel-track]').replaceChildren(...feed.cards);
        rows.push(row);
      }
      this.#loadedKey = stateKey;
      this.#emptyResult = !rows.length;
      this.replaceChildren(...rows);
    } finally {
      this.#loading = false;
      if (this.isConnected && this.#stateKey() !== stateKey) this.#loadIfVisible();
    }
  };
}

class CartRecommendationCarousel extends HTMLElement {
  #controller;
  #resizeObserver;
  connectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const track = this.querySelector('[data-carousel-track]');
    const previous = this.querySelector('[data-carousel-previous]');
    const next = this.querySelector('[data-carousel-next]');
    if (!track || !previous || !next) return;
    const update = () => {
      const position = Math.abs(track.scrollLeft);
      previous.setAttribute('aria-disabled', String(position < 2));
      next.setAttribute('aria-disabled', String(position >= track.scrollWidth - track.clientWidth - 2));
    };
    const move = direction => {
      const width = track.firstElementChild?.getBoundingClientRect().width || track.clientWidth;
      const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      track.scrollBy({ left: direction * rtl * (width + 12), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });
    track.addEventListener('scroll', update, { signal, passive: true });
    this.#resizeObserver = new ResizeObserver(update);
    this.#resizeObserver.observe(track);
    update();
  }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
  }
}

if (!customElements.get('native-cart-recommendations')) customElements.define('native-cart-recommendations', NativeCartRecommendations);
if (!customElements.get('cart-recommendation-carousel')) customElements.define('cart-recommendation-carousel', CartRecommendationCarousel);
